/**
 * The ONE centred dialog (WS-Z7 / R1) — the middle-of-screen counterpart to `ui/Drawer.tsx`.
 *
 * The inventory found two hand-rolled `.modal` markups beside this component (the speed test
 * and Generate's result preview), each re-implementing the same four elements. The speed
 * test's closed on its own X and on a click outside, but not on Escape, and Tab could leave
 * the panel for the page behind it. This is the survivor; the chrome every dialog owes —
 * focus in, focus back to the opener, Tab trapped, Escape, the heading as the accessible
 * name — is `ui/dialog.ts`, shared verbatim with the Drawer.
 *
 * It comes in TWO shapes, because the surfaces that have to adopt it are not all forms:
 *
 *   • **titled** (`title` + optional `subtitle` / `badge` / `footer`) — a form or a detail
 *     panel, with a `.modal__head` above the body;
 *   • **bare** (`bare` + `label`) — chrome-less and full-bleed, for a viewer whose CONTENT is
 *     the point: an image lightbox, a raw trace. No `.modal__head`, no panel surface, and the
 *     accessible name comes from `label` because there is no heading to point at. Everything
 *     else — the focus trap, Escape, the overlay click, the focus return — is identical.
 */
import type { ReactNode } from "react";
import { Icon } from "./Icon.js";
import { DialogStackProvider, useDialogChrome } from "./dialog.js";
import "./modal.css";

interface ModalCommon {
  onClose: () => void;
  children: ReactNode;
  testid?: string;
  /** Action bar pinned to the bottom of the modal — an `ActionBar` belongs here. */
  footer?: ReactNode;
}

export interface TitledModalProps extends ModalCommon {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Status pill shown top-right, before the close button. */
  badge?: ReactNode;
  /** A wider panel for dialogs that carry a grid of results rather than a form. */
  wide?: boolean;
  bare?: false;
  label?: never;
}

export interface BareModalProps extends ModalCommon {
  /** Chrome-less and full-bleed: no `.modal__head`, no panel surface under the content. */
  bare: true;
  /**
   * REQUIRED, and the reason `bare` is a separate props shape rather than an optional
   * `title`: `aria-modal` with no name announces the word "dialog" and nothing else, and a
   * bare dialog has no heading that could supply one.
   */
  label: string;
  title?: never;
  subtitle?: never;
  badge?: never;
  wide?: never;
}

export type ModalProps = TitledModalProps | BareModalProps;

export function Modal(props: ModalProps) {
  const { footer, onClose, children, testid } = props;
  const { panelRef, closeRef, titleId, onOverlayClick, dialog } = useDialogChrome(onClose);
  const bare = props.bare === true;

  const panelClass = [
    "modal__panel",
    bare ? "modal__panel--bare" : "",
    !bare && props.wide ? "modal__panel--wide" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const close = (
    <button
      ref={closeRef}
      type="button"
      className={bare ? "modal__close modal__close--floating" : "modal__close"}
      onClick={onClose}
      data-testid="modal-close"
      aria-label="Close"
    >
      <Icon name="close" size={16} />
    </button>
  );

  return (
    // Everything this dialog renders is rendered INSIDE it, so a dialog opened from inside
    // it orders above it — including one whose panel is portaled elsewhere (review-2 F-10).
    <DialogStackProvider dialog={dialog}>
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      // One name, never two: the titled shape points at its heading, the bare shape carries
      // the string it was given. A dialog with both is a name nobody can predict.
      {...(bare ? { "aria-label": props.label } : { "aria-labelledby": titleId })}
      data-testid={testid}
    >
      <div className="modal__overlay" onClick={onOverlayClick} aria-hidden="true" />
      <div className={panelClass} role="document" ref={panelRef}>
        {bare ? (
          close
        ) : (
          <header className="modal__head">
            <div className="modal__heading">
              <h2 className="modal__title" id={titleId}>{props.title}</h2>
              {props.subtitle && <p className="modal__subtitle">{props.subtitle}</p>}
            </div>
            <div className="modal__head-actions">
              {props.badge}
              {close}
            </div>
          </header>
        )}
        <div className="modal__body">{children}</div>
        {footer && <footer className="modal__footer" data-testid="modal-footer">{footer}</footer>}
      </div>
    </div>
    </DialogStackProvider>
  );
}
